import React, { useState } from 'react';
import {
  X,
  Globe,
  Plus,
  Trash2,
  ExternalLink,
  HelpCircle,
  Sparkles,
  Link as LinkIcon,
  MessageCircle,
  Mail,
  Instagram,
  Facebook,
  Twitter,
  Image as ImageIcon,
  Video,
  Layers,
  ChevronDown,
  ChevronUp,
  Check,
  Compass,
  ArrowRight,
  ShieldCheck,
  LayoutGrid,
  Megaphone,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Sliders
} from 'lucide-react';
import { useStorefrontData } from '../../context/StorefrontDataContext';
import InsertImageModal from '../blog/InsertImageModal';
import logoTypeDark from '../../Logo/LogoType-dark.svg';
import logoTypeLight from '../../Logo/LogoType-light.svg';
import { initialCMSContent } from '../../data/cms';

const COMMON_ROUTE_OPTIONS = [
  { label: 'Katalog Koleksi', url: '/shop' },
  { label: 'Tentang Kami', url: '/about' },
  { label: 'Karir & Lowongan', url: '/careers' },
  { label: 'Blog & Artikel', url: '/blog' },
  { label: 'Syarat & Ketentuan', url: '/terms' },
  { label: 'Kebijakan Privasi', url: '/privacy-policy' },
  { label: 'Pengiriman & Retur', url: '/shipping-policy' },
];

export default function GlobalMasterDrawer() {
  const {
    cms,
    isMasterDrawerOpen,
    activeMasterTab,
    closeMasterDrawer,
    updateCmsField,
    updateWholeCms,
    saveStatus,
    lastSavedTime,
    saveDraftManual,
    addFooterColumn,
    removeFooterColumn,
    updateFooterColumnTitle,
    addFooterLinkItem,
    removeFooterLinkItem,
    updateFooterLinkItem
  } = useStorefrontData();

  const [currentTab, setCurrentTab] = useState<'footer' | 'cta' | 'header'>('footer');
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageModalTarget, setImageModalTarget] = useState<'cta' | 'logoLight' | 'logoDark' | null>(null);
  const [expandedCol, setExpandedCol] = useState<number | null>(0);

  // Sync tab with activeMasterTab when drawer opens
  React.useEffect(() => {
    if (activeMasterTab) {
      setCurrentTab(activeMasterTab);
    }
  }, [activeMasterTab, isMasterDrawerOpen]);

  if (!isMasterDrawerOpen) return null;

  const footer = cms?.footer || {};
  const cta = cms?.cta || {};
  const linkGroups = Array.isArray(footer.links) ? footer.links : [];

  const header = (cms as any)?.header || initialCMSContent.header || {
    announcement: { enabled: false, text: '', link: '' },
    logo: { lightUrl: '', darkUrl: '', heightPx: 20 },
    menuItems: []
  };

  const announcement = header.announcement || { enabled: false, text: '', link: '' };
  const logo = header.logo || { lightUrl: '', darkUrl: '', heightPx: 20 };
  const menuItems = Array.isArray(header.menuItems) && header.menuItems.length > 0
    ? header.menuItems
    : (initialCMSContent.header?.menuItems || []);

  const handleUpdateAnnouncement = (field: string, value: any) => {
    updateCmsField('header', 'announcement', {
      ...announcement,
      [field]: value
    });
  };

  const handleUpdateLogo = (field: string, value: any) => {
    updateCmsField('header', 'logo', {
      ...logo,
      [field]: value
    });
  };

  const handleResetLogo = () => {
    updateCmsField('header', 'logo', {
      lightUrl: '',
      darkUrl: '',
      heightPx: 20
    });
  };

  const handleAddMenuItem = () => {
    const newItem = {
      id: `nav-${Date.now()}`,
      label: 'Menu Baru',
      url: '/shop'
    };
    updateCmsField('header', 'menuItems', [...menuItems, newItem]);
  };

  const handleUpdateMenuItem = (index: number, field: string, value: any) => {
    const next = [...menuItems];
    next[index] = { ...next[index], [field]: value };
    updateCmsField('header', 'menuItems', next);
  };

  const handleRemoveMenuItem = (index: number) => {
    const next = menuItems.filter((_: any, i: number) => i !== index);
    updateCmsField('header', 'menuItems', next);
  };

  const handleMoveMenuItem = (index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= menuItems.length) return;
    const next = [...menuItems];
    const temp = next[index];
    next[index] = next[targetIdx];
    next[targetIdx] = temp;
    updateCmsField('header', 'menuItems', next);
  };

  const handleResetDefaultMenu = () => {
    updateCmsField('header', 'menuItems', [
      { id: "nav-home", label: "Beranda", url: "/" },
      { id: "nav-shop", label: "Koleksi", url: "/shop" },
      { id: "nav-about", label: "Tentang", url: "/about" },
      { id: "nav-careers", label: "Karir", url: "/careers" },
      { id: "nav-blog", label: "Blog", url: "/blog" }
    ]);
  };

  const scrollToFooter = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const scrollToCta = () => {
    document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-end bg-black/25 backdrop-blur-[2px] transition-opacity animate-fade-in overflow-hidden h-[100dvh]"
      data-lenis-prevent="true"
    >
      {/* Backdrop overlay */}
      <div className="flex-1 cursor-pointer" onClick={closeMasterDrawer} />

      {/* Drawer panel */}
      <div
        className="w-full max-w-lg bg-[#FCFCFB] text-ink h-full h-[100dvh] max-h-[100dvh] shadow-2xl flex flex-col border-l border-border-subtle z-10 overflow-hidden animate-slide-left relative"
        data-lenis-prevent="true"
      >
        {/* Header */}
        <div className="px-6 py-4.5 border-b border-border-subtle flex items-center justify-between bg-white shrink-0">
          <div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                Master Component Sitewide
              </span>
            </div>
            <h2 className="text-lg font-serif font-bold text-ink mt-1">Komponen & Layout Global</h2>
            <p className="text-xs text-muted">Perubahan berlaku di seluruh halaman website.</p>
          </div>
          <button
            type="button"
            onClick={closeMasterDrawer}
            className="p-1.5 text-muted hover:text-ink hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            title="Tutup Panel Master"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab switch */}
        <div className="flex border-b border-border-subtle bg-gray-50/80 px-4 shrink-0">
          <button
            type="button"
            onClick={() => {
              setCurrentTab('footer');
              scrollToFooter();
            }}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-colors cursor-pointer flex items-center gap-2 ${
              currentTab === 'footer'
                ? 'border-brand-wine text-brand-wine bg-white'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            <span>Master Footer</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setCurrentTab('cta');
              scrollToCta();
            }}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-colors cursor-pointer flex items-center gap-2 ${
              currentTab === 'cta'
                ? 'border-brand-wine text-brand-wine bg-white'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Master CTA Banner</span>
          </button>
          <button
            type="button"
            onClick={() => setCurrentTab('header')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-colors cursor-pointer flex items-center gap-2 ${
              currentTab === 'header'
                ? 'border-brand-wine text-brand-wine bg-white'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            <Compass className="w-3.5 h-3.5" />
            <span>Header & Nav</span>
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6 space-y-6 touch-pan-y"
          data-lenis-prevent="true"
        >
          {/* TAB 1: MASTER FOOTER */}
          {currentTab === 'footer' && (
            <div className="space-y-6">
              {/* Informational Callout */}
              <div className="p-3.5 bg-amber-50/90 rounded-xl border border-amber-200/80 text-amber-900 text-xs leading-relaxed flex items-start gap-2.5">
                <Globe className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <strong className="font-semibold block text-amber-950">Footer Terintegrasi Sitewide</strong>
                  Semua halaman toko memuat susunan footer ini secara seragam. Anda dapat menambah atau mengurangi kolom navigasi secara dinamis.
                </div>
              </div>

              {/* 1. Dynamic Footer Link Columns */}
              <div className="bg-white rounded-xl border border-border-subtle p-4.5 space-y-4 shadow-2xs">
                <div className="flex items-center justify-between border-b border-border-subtle pb-3">
                  <div>
                    <h3 className="text-sm font-bold text-ink">Kolom Tautan Navigasi (Dinamis)</h3>
                    <p className="text-[11px] text-muted">Atur grup kolom dan daftar tautan internal/eksternal.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => addFooterColumn(`Kolom ${linkGroups.length + 1}`)}
                    className="px-2.5 py-1.5 bg-[#722F38] hover:bg-[#5E272E] text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow-xs transition-transform active:scale-95 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tambah Kolom</span>
                  </button>
                </div>

                {linkGroups.length === 0 ? (
                  <div className="py-6 text-center text-xs text-muted bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    Belum ada kolom tautan. Klik "+ Tambah Kolom" di atas.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {linkGroups.map((group: any, colIdx: number) => {
                      const isExpanded = expandedCol === colIdx;
                      return (
                        <div
                          key={group.id || colIdx}
                          className="border border-border-subtle rounded-xl overflow-hidden bg-[#FCFCFB]"
                        >
                          {/* Column Header */}
                          <div className="p-3 bg-white flex items-center justify-between gap-2 border-b border-border-subtle">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-[10px] font-mono font-bold text-brand-wine/70 px-1.5 py-0.5 bg-brand-wine/5 rounded">
                                #{colIdx + 1}
                              </span>
                              <input
                                type="text"
                                value={group.title || ''}
                                onChange={(e) => updateFooterColumnTitle(colIdx, e.target.value)}
                                placeholder="Judul Kolom (misal: Koleksi)"
                                className="text-xs font-bold text-ink bg-transparent border-b border-transparent hover:border-gray-300 focus:border-brand-wine focus:outline-hidden px-1 py-0.5 flex-1 transition-colors"
                              />
                            </div>

                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => setExpandedCol(isExpanded ? null : colIdx)}
                                className="p-1 text-muted hover:text-ink hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                title="Buka/Tutup Kolom"
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm(`Hapus kolom "${group.title || 'ini'}" beserta isinya?`)) {
                                    removeFooterColumn(colIdx);
                                  }
                                }}
                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                title="Hapus Kolom Ini"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Column Items */}
                          {isExpanded && (
                            <div className="p-3 space-y-2.5">
                              {(!group.items || group.items.length === 0) ? (
                                <p className="text-[11px] text-muted italic">Belum ada tautan di kolom ini.</p>
                              ) : (
                                group.items.map((item: any, itemIdx: number) => (
                                  <div
                                    key={item.id || itemIdx}
                                    className="p-2 bg-white rounded-lg border border-border-subtle space-y-1.5 shadow-2xs"
                                  >
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={item.label || ''}
                                        onChange={(e) => updateFooterLinkItem(colIdx, itemIdx, 'label', e.target.value)}
                                        placeholder="Label Tautan (misal: Versa)"
                                        className="text-xs font-medium text-ink border border-gray-200 rounded px-2 py-1 flex-1 focus:border-brand-wine focus:outline-hidden"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeFooterLinkItem(colIdx, itemIdx)}
                                        className="p-1 text-muted hover:text-red-600 rounded transition-colors cursor-pointer"
                                        title="Hapus Link"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                      <input
                                        type="text"
                                        value={item.url || ''}
                                        onChange={(e) => updateFooterLinkItem(colIdx, itemIdx, 'url', e.target.value)}
                                        placeholder="URL tujuan (misal: /shop/versa)"
                                        className="text-[11px] font-mono text-gray-700 border border-gray-200 rounded px-2 py-1 flex-1 focus:border-brand-wine focus:outline-hidden"
                                      />
                                      <select
                                        onChange={(e) => {
                                          if (e.target.value) {
                                            updateFooterLinkItem(colIdx, itemIdx, 'url', e.target.value);
                                          }
                                        }}
                                        defaultValue=""
                                        className="text-[10px] text-muted border border-gray-200 rounded px-1.5 py-1 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                                        title="Pilih shortcut rute website"
                                      >
                                        <option value="" disabled>Pilih Rute</option>
                                        {COMMON_ROUTE_OPTIONS.map((route) => (
                                          <option key={route.url} value={route.url}>
                                            {route.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                ))
                              )}

                              <button
                                type="button"
                                onClick={() => addFooterLinkItem(colIdx, { label: 'Tautan Baru', url: '/shop' })}
                                className="w-full py-1.5 text-xs text-brand-wine font-semibold hover:bg-brand-wine/5 border border-dashed border-brand-wine/30 rounded-lg flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Tambah Tautan ke #{group.title || `Kolom ${colIdx + 1}`}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 2. Affiliate Program / Campaign Banner */}
              <div className="bg-white rounded-xl border border-border-subtle p-4.5 space-y-3.5 shadow-2xs">
                <h3 className="text-sm font-bold text-ink border-b border-border-subtle pb-2">
                  Banner Program Afiliasi / Promo
                </h3>

                <div>
                  <label className="text-[11px] font-semibold text-muted block mb-1">Subjudul Kampanye</label>
                  <input
                    type="text"
                    value={footer.campaignSubtitle || ''}
                    onChange={(e) => updateCmsField('footer', 'campaignSubtitle', e.target.value)}
                    placeholder="misal: Dapatkan komisi eksklusif..."
                    className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-muted block mb-1">Judul Utama Kampanye</label>
                  <input
                    type="text"
                    value={footer.campaignTitle || ''}
                    onChange={(e) => updateCmsField('footer', 'campaignTitle', e.target.value)}
                    placeholder="misal: BERGABUNG SEBAGAI AFFILIATOR SECERA"
                    className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-serif font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-muted block mb-1">Teks Tombol CTA</label>
                    <input
                      type="text"
                      value={footer.campaignCtaText || ''}
                      onChange={(e) => updateCmsField('footer', 'campaignCtaText', e.target.value)}
                      placeholder="HUBUNGI WHATSAPP"
                      className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-muted block mb-1">Tautan Tombol</label>
                    <input
                      type="text"
                      value={footer.campaignCtaLink || ''}
                      onChange={(e) => updateCmsField('footer', 'campaignCtaLink', e.target.value)}
                      placeholder="https://wa.me/628..."
                      className="w-full text-xs font-mono text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                    />
                  </div>
                </div>
              </div>

              {/* 3. Contact & Social Media */}
              <div className="bg-white rounded-xl border border-border-subtle p-4.5 space-y-3.5 shadow-2xs">
                <h3 className="text-sm font-bold text-ink border-b border-border-subtle pb-2">
                  Kontak Layanan & Media Sosial
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-muted block mb-1">Email Layanan</label>
                    <div className="relative">
                      <Mail className="w-3.5 h-3.5 text-muted absolute left-2.5 top-2.5" />
                      <input
                        type="email"
                        value={footer.email || ''}
                        onChange={(e) => updateCmsField('footer', 'email', e.target.value)}
                        placeholder="care@secera.id"
                        className="w-full text-xs text-ink pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:border-brand-wine focus:outline-hidden"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-muted block mb-1">Nomor WhatsApp CS</label>
                    <div className="relative">
                      <MessageCircle className="w-3.5 h-3.5 text-muted absolute left-2.5 top-2.5" />
                      <input
                        type="text"
                        value={footer.phone || ''}
                        onChange={(e) => updateCmsField('footer', 'phone', e.target.value)}
                        placeholder="085750990000"
                        className="w-full text-xs text-ink pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:border-brand-wine focus:outline-hidden"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <label className="text-[11px] font-semibold text-muted block">Tautan Akun Sosial Media</label>
                  
                  <div className="space-y-2 text-xs">
                    <input
                      type="text"
                      value={footer.instagramUrl || ''}
                      onChange={(e) => updateCmsField('footer', 'instagramUrl', e.target.value)}
                      placeholder="Instagram URL (https://instagram.com/secera.id)"
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-mono text-[11px]"
                    />
                    <input
                      type="text"
                      value={footer.tiktokUrl || ''}
                      onChange={(e) => updateCmsField('footer', 'tiktokUrl', e.target.value)}
                      placeholder="TikTok URL (https://tiktok.com/@secera.id)"
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-mono text-[11px]"
                    />
                    <input
                      type="text"
                      value={footer.shopeeUrl || ''}
                      onChange={(e) => updateCmsField('footer', 'shopeeUrl', e.target.value)}
                      placeholder="Shopee Store URL (https://shopee.co.id/...)"
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-mono text-[11px]"
                    />
                    <input
                      type="text"
                      value={footer.whatsappUrl || ''}
                      onChange={(e) => updateCmsField('footer', 'whatsappUrl', e.target.value)}
                      placeholder="WhatsApp Direct Link (opsional khusus sosmed)"
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-mono text-[11px]"
                    />
                  </div>
                </div>
              </div>

              {/* 4. Tagline & Copyright */}
              <div className="bg-white rounded-xl border border-border-subtle p-4.5 space-y-3.5 shadow-2xs">
                <h3 className="text-sm font-bold text-ink border-b border-border-subtle pb-2">
                  Tagline & Teks Hak Cipta
                </h3>

                <div>
                  <label className="text-[11px] font-semibold text-muted block mb-1">Slogan / Tagline Brand</label>
                  <input
                    type="text"
                    value={footer.tagline || ''}
                    onChange={(e) => updateCmsField('footer', 'tagline', e.target.value)}
                    placeholder="Mendefinisikan ulang gaya modest"
                    className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-muted block mb-1">Teks Hak Cipta (Copyright)</label>
                  <input
                    type="text"
                    value={footer.copyright || ''}
                    onChange={(e) => updateCmsField('footer', 'copyright', e.target.value)}
                    placeholder="©Copyright 2026 Secera"
                    className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: MASTER CTA BANNER */}
          {currentTab === 'cta' && (
            <div className="space-y-6">
              <div className="p-3.5 bg-amber-50/90 rounded-xl border border-amber-200/80 text-amber-900 text-xs leading-relaxed flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <strong className="font-semibold block text-amber-950">Master Global CTA</strong>
                  Banner ini adalah penawaran pamungkas utama yang tampil sebelum footer. Secara default, seluruh halaman mewarisi banner ini. Anda dapat meng-override penawaran khusus di halaman tertentu.
                </div>
              </div>

              <div className="bg-white rounded-xl border border-border-subtle p-4.5 space-y-4 shadow-2xs">
                <div className="flex items-center justify-between border-b border-border-subtle pb-2">
                  <h3 className="text-sm font-bold text-ink">Konten Master Banner CTA</h3>
                  <button
                    type="button"
                    onClick={scrollToCta}
                    className="text-xs text-brand-wine hover:underline flex items-center gap-1 font-semibold"
                  >
                    <span>Scroll ke Seksi</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-muted block mb-1">Headline Utama</label>
                  <textarea
                    rows={2}
                    value={cta.title || ''}
                    onChange={(e) => updateCmsField('cta', 'title', e.target.value)}
                    placeholder="Hari spesial, bersama SECERA."
                    className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-serif"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-muted block mb-1">Deskripsi Tambahan</label>
                  <textarea
                    rows={3}
                    value={cta.description || ''}
                    onChange={(e) => updateCmsField('cta', 'description', e.target.value)}
                    placeholder="Saat momen spesial datang, kamu ingin tampil tepat..."
                    className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden leading-relaxed"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-muted block mb-1">Teks Tombol</label>
                    <input
                      type="text"
                      value={cta.buttonText || ''}
                      onChange={(e) => updateCmsField('cta', 'buttonText', e.target.value)}
                      placeholder="Pilih VERSA untuk Hari Spesial"
                      className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden font-semibold"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-muted block mb-1">Tautan Tombol</label>
                    <input
                      type="text"
                      value={cta.buttonLink || ''}
                      onChange={(e) => updateCmsField('cta', 'buttonLink', e.target.value)}
                      placeholder="/product/versa atau /shop"
                      className="w-full text-xs font-mono text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-border-subtle space-y-3">
                  <label className="text-[11px] font-semibold text-muted block">Background Media (Foto / Video)</label>

                  <div>
                    <span className="text-[10px] text-muted block mb-1">URL Gambar Background</span>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={cta.backgroundImageUrl || ''}
                        onChange={(e) => updateCmsField('cta', 'backgroundImageUrl', e.target.value)}
                        placeholder="https://... atau /uploads/..."
                        className="w-full text-[11px] font-mono text-ink border border-gray-200 rounded-lg px-3 py-1.5 focus:border-brand-wine focus:outline-hidden"
                      />
                      <button
                        type="button"
                        onClick={() => setShowImageModal(true)}
                        className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg flex items-center gap-1 shrink-0 transition-colors cursor-pointer"
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                        <span>Pilih Foto</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] text-muted block mb-1">URL Video Background (MP4 / WebM / CDN)</span>
                    <input
                      type="text"
                      value={cta.backgroundVideoUrl || ''}
                      onChange={(e) => updateCmsField('cta', 'backgroundVideoUrl', e.target.value)}
                      placeholder="/uploads/...mp4 atau link Google Drive"
                      className="w-full text-[11px] font-mono text-ink border border-gray-200 rounded-lg px-3 py-1.5 focus:border-brand-wine focus:outline-hidden"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: HEADER & NAV */}
          {currentTab === 'header' && (
            <div className="space-y-6">
              <div className="p-3.5 bg-blue-50/90 rounded-xl border border-blue-200/80 text-blue-900 text-xs leading-relaxed flex items-start gap-2.5">
                <Compass className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <strong className="font-semibold block text-blue-950">Header & Navigasi Toko</strong>
                  Navbar atas SECERA mengintegrasikan Logo, Menu Koleksi, Keranjang Belanja, dan Akun Pelanggan secara responsif di seluruh storefront.
                </div>
              </div>

              {/* 1. TOP ANNOUNCEMENT BAR */}
              <div className="bg-white rounded-xl border border-border-subtle p-5 space-y-4 shadow-2xs">
                <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-brand-wine" />
                    <div>
                      <h3 className="text-xs font-bold text-ink uppercase tracking-wider">
                        Top Announcement Bar
                      </h3>
                      <p className="text-[11px] text-muted">
                        Pita promosi sticky melayang di puncak halaman
                      </p>
                    </div>
                  </div>
                  {/* Switch Toggle */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(announcement.enabled)}
                      onChange={(e) => handleUpdateAnnouncement('enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-wine"></div>
                  </label>
                </div>

                {announcement.enabled ? (
                  <div className="space-y-3 animate-fade-in">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Teks Pengumuman Promo
                      </label>
                      <input
                        type="text"
                        value={announcement.text || ''}
                        onChange={(e) => handleUpdateAnnouncement('text', e.target.value)}
                        placeholder="Contoh: Gratis Ongkir ke Seluruh Indonesia Hari Ini"
                        className="w-full text-xs text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Tautan Rute / Link (Opsional)
                      </label>
                      <input
                        type="text"
                        value={announcement.link || ''}
                        onChange={(e) => handleUpdateAnnouncement('link', e.target.value)}
                        placeholder="Contoh: /shop atau https://..."
                        className="w-full text-xs font-mono text-ink border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-wine focus:outline-hidden"
                      />
                    </div>

                    {/* Live Preview Bar */}
                    <div className="pt-1">
                      <span className="text-[10px] font-mono text-muted uppercase tracking-wider block mb-1">
                        Pratinjau Tampilan Bar:
                      </span>
                      <div className="bg-[#722F38] text-white text-[11px] font-medium py-1.5 px-3 rounded-md text-center flex items-center justify-center gap-1.5 shadow-xs">
                        <span>{announcement.text || 'Teks pengumuman akan muncul di sini...'}</span>
                        {announcement.link && <span className="opacity-70 text-[10px]">→</span>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted italic">
                    Announcement Bar saat ini nonaktif. Aktifkan sakelar di atas untuk menampilkannya di storefront.
                  </p>
                )}
              </div>

              {/* 2. LOGO TOKO & BRANDING */}
              <div className="bg-white rounded-xl border border-border-subtle p-5 space-y-4 shadow-2xs">
                <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-brand-wine" />
                    <div>
                      <h3 className="text-xs font-bold text-ink uppercase tracking-wider">
                        Logo Toko & Identitas Brand
                      </h3>
                      <p className="text-[11px] text-muted">
                        Slot logo terang (navbar transparan) & gelap (navbar putih)
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleResetLogo}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-gray-600 hover:text-brand-wine bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
                    title="Kembalikan ke Logo Tipografi SVG resmi SECERA"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset Default</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {/* Slot 1: Logo Terang (Light Mode) */}
                  <div className="p-3 bg-zinc-900 rounded-xl border border-zinc-800 space-y-2 text-white">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase text-zinc-400 font-bold">
                        1. Logo Terang (Navbar Transparan)
                      </span>
                      {logo.lightUrl && (
                        <button
                          type="button"
                          onClick={() => handleUpdateLogo('lightUrl', '')}
                          className="text-zinc-400 hover:text-red-400 text-[10px] cursor-pointer"
                        >
                          Hapus
                        </button>
                      )}
                    </div>
                    <div className="h-14 bg-zinc-800/80 rounded-lg flex items-center justify-center p-2 border border-zinc-700/60 overflow-hidden">
                      <img
                        src={logo.lightUrl || logoTypeLight}
                        alt="Logo Terang"
                        className="max-h-8 max-w-full object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setImageModalTarget('logoLight');
                        setShowImageModal(true);
                      }}
                      className="w-full py-1.5 px-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-medium rounded-lg border border-zinc-700 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      <span>{logo.lightUrl ? 'Ganti Logo Terang' : 'Pilih Logo Terang'}</span>
                    </button>
                  </div>

                  {/* Slot 2: Logo Gelap (Dark Mode) */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2 text-ink">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase text-muted font-bold">
                        2. Logo Gelap (Navbar Putih Solid)
                      </span>
                      {logo.darkUrl && (
                        <button
                          type="button"
                          onClick={() => handleUpdateLogo('darkUrl', '')}
                          className="text-muted hover:text-red-600 text-[10px] cursor-pointer"
                        >
                          Hapus
                        </button>
                      )}
                    </div>
                    <div className="h-14 bg-white rounded-lg flex items-center justify-center p-2 border border-gray-200 overflow-hidden">
                      <img
                        src={logo.darkUrl || logoTypeDark}
                        alt="Logo Gelap"
                        className="max-h-8 max-w-full object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setImageModalTarget('logoDark');
                        setShowImageModal(true);
                      }}
                      className="w-full py-1.5 px-2 bg-white hover:bg-gray-100 text-ink text-[11px] font-medium rounded-lg border border-gray-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      <span>{logo.darkUrl ? 'Ganti Logo Gelap' : 'Pilih Logo Gelap'}</span>
                    </button>
                  </div>
                </div>

                {/* Logo Height Control */}
                <div className="p-3 bg-gray-50/80 rounded-xl border border-gray-200/80 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Sliders className="w-3.5 h-3.5 text-muted" />
                    <span className="text-xs font-semibold text-gray-700">Tinggi Logo Tampil:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={16}
                      max={36}
                      value={logo.heightPx || 20}
                      onChange={(e) => handleUpdateLogo('heightPx', Number(e.target.value))}
                      className="w-28 accent-brand-wine cursor-pointer"
                    />
                    <span className="text-xs font-mono font-bold text-brand-wine w-10 text-right">
                      {logo.heightPx || 20}px
                    </span>
                  </div>
                </div>
              </div>

              {/* 3. MENU NAVIGASI HEADER */}
              <div className="bg-white rounded-xl border border-border-subtle p-5 space-y-4 shadow-2xs">
                <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
                  <div className="flex items-center gap-2">
                    <Compass className="w-4 h-4 text-brand-wine" />
                    <div>
                      <h3 className="text-xs font-bold text-ink uppercase tracking-wider">
                        Menu Navigasi Toko ({menuItems.length})
                      </h3>
                      <p className="text-[11px] text-muted">
                        Otomatis tersinkronisasi di Desktop dan Mobile Drawer
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddMenuItem}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-wine text-white text-xs font-bold rounded-lg hover:bg-brand-wine-deep shadow-xs transition-colors cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tambah Menu</span>
                  </button>
                </div>

                <div className="space-y-2.5">
                  {menuItems.map((item: any, idx: number) => (
                    <div
                      key={item.id || idx}
                      className="p-3 rounded-xl border border-border-subtle/80 bg-gray-50/50 hover:bg-white hover:border-brand-wine/40 transition-all flex flex-col md:flex-row items-stretch md:items-center gap-2.5 group"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-md bg-gray-200 text-gray-700 text-[10px] font-mono font-bold flex items-center justify-center shrink-0">
                          {idx + 1}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() => handleMoveMenuItem(idx, 'up')}
                            disabled={idx === 0}
                            className="p-0.5 text-gray-400 hover:text-ink disabled:opacity-20 cursor-pointer"
                            title="Pindah ke atas"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveMenuItem(idx, 'down')}
                            disabled={idx === menuItems.length - 1}
                            className="p-0.5 text-gray-400 hover:text-ink disabled:opacity-20 cursor-pointer"
                            title="Pindah ke bawah"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {/* Label Input */}
                      <div className="flex-1 min-w-[120px]">
                        <input
                          type="text"
                          value={item.label || ''}
                          onChange={(e) => handleUpdateMenuItem(idx, 'label', e.target.value)}
                          placeholder="Label Menu"
                          className="w-full text-xs font-bold text-ink border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-brand-wine focus:outline-hidden"
                        />
                      </div>

                      {/* URL Input */}
                      <div className="flex-1 min-w-[140px]">
                        <input
                          type="text"
                          value={item.url || ''}
                          onChange={(e) => handleUpdateMenuItem(idx, 'url', e.target.value)}
                          placeholder="Rute (/shop, /about)"
                          className="w-full text-xs font-mono text-ink border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-brand-wine focus:outline-hidden"
                        />
                      </div>

                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveMenuItem(idx)}
                        className="p-1.5 text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer shrink-0 self-end md:self-center"
                        title="Hapus Menu Ini"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="pt-2 flex items-center justify-between border-t border-border-subtle/80">
                  <span className="text-[11px] text-muted">
                    Gunakan awalan <code>/</code> untuk rute internal, atau <code>https://</code> untuk link eksternal.
                  </span>
                  <button
                    type="button"
                    onClick={handleResetDefaultMenu}
                    className="text-[11px] text-brand-wine hover:underline font-medium cursor-pointer"
                  >
                    Kembalikan Menu Standar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Status & Manual Save Bar */}
        <div className="p-4 bg-white border-t border-border-subtle flex items-center justify-between gap-3 shrink-0">
          <div className="text-[11px] text-muted">
            {saveStatus === 'saving' ? (
              <span className="text-amber-600 font-semibold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
                Menyimpan draf global...
              </span>
            ) : saveStatus === 'saved' ? (
              <span className="text-emerald-600 font-medium flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                Draf tersimpan {lastSavedTime ? `pk ${lastSavedTime}` : ''}
              </span>
            ) : (
              <span>Draf otomatis tersimpan</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={saveDraftManual}
              className="px-4 py-2 bg-[#722F38] hover:bg-[#5E272E] text-white text-xs font-bold rounded-lg shadow-sm transition-transform active:scale-95 cursor-pointer"
            >
              Simpan Draf
            </button>
          </div>
        </div>
      </div>

      {/* Insert Image Modal for CTA & Logo */}
      <InsertImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setImageModalTarget(null);
        }}
        onInsert={(url) => {
          if (imageModalTarget === 'cta') {
            updateCmsField('cta', 'backgroundImageUrl', url);
          } else if (imageModalTarget === 'logoLight') {
            handleUpdateLogo('lightUrl', url);
          } else if (imageModalTarget === 'logoDark') {
            handleUpdateLogo('darkUrl', url);
          }
          setShowImageModal(false);
          setImageModalTarget(null);
        }}
      />
    </div>
  );
}
